import {
  type SidebarDefinition,
  type SidebarOperation,
  type SidebarSnapshot,
  defineSidebar,
  readSidebar,
} from "@/types/sidebar-session";

import { useRightSidebarStore } from "@/stores/right-sidebar-store";

export interface SetupFocusRequest {
  section: string;
  itemId: string;
}

export enum SetupSidebarCommand {
  CONSUME_FOCUS = "CONSUME_FOCUS",
}

interface SetupSidebarPayload {
  focus: SetupFocusRequest | null;
}

type SetupSidebarInput = { focus?: SetupFocusRequest } | SetupSidebarCommand;
const EMPTY_SETUP: SetupSidebarPayload = { focus: null };

export const setupSidebarDefinition: SidebarDefinition<
  SetupSidebarInput,
  SetupSidebarPayload
> = defineSidebar<SetupSidebarInput, SetupSidebarPayload>({
  id: "SETUP",
  label: "Setup",
  order: 30,
  isAvailable: (environment) => environment.capabilities.SETUP !== false,
  strategy: {
    planOpen(context, input) {
      if (input === SetupSidebarCommand.CONSUME_FOCUS) {
        if (!context.read(setupSidebarDefinition)) return {};
        return { upsert: [context.write(setupSidebarDefinition, EMPTY_SETUP)] };
      }
      return {
        upsert: [
          context.write(setupSidebarDefinition, { focus: input.focus ?? null }),
        ],
        select: setupSidebarDefinition.id,
      };
    },
  },
});

export function selectSetupSidebar(
  state: Pick<SidebarSnapshot, "entries">,
): SetupSidebarPayload {
  return readSidebar(state, setupSidebarDefinition)?.payload ?? EMPTY_SETUP;
}

interface SetupArrival {
  ownerPath: string;
  nextPath: string;
  focus: SetupFocusRequest;
  operation: SidebarOperation;
}
let arrival: SetupArrival | null = null;

export function cancelSetupSidebarArrival() {
  const pending = arrival;
  arrival = null;
  pending?.operation.dispose();
}

/** A feature-owned navigation request, never a hidden sidebar entry. */
export function scheduleSetupSidebarArrival(
  focus: SetupFocusRequest,
  nextPath: string,
) {
  cancelSetupSidebarArrival();
  const store = useRightSidebarStore.getState();
  // Close before leasing: closing the full guide's outgoing panel must not
  // invalidate the request we are about to schedule.
  store.close(setupSidebarDefinition.id);
  const operation = store.beginOperation(setupSidebarDefinition.id);
  const pending = {
    ownerPath: store.environment.path,
    nextPath,
    focus,
    operation,
  };
  arrival = pending;
  operation.signal.addEventListener(
    "abort",
    () => {
      if (arrival === pending) arrival = null;
    },
    { once: true },
  );
}

export function reconcileSetupSidebarArrival(path: string) {
  const pending = arrival;
  if (!pending) return;
  if (!pending.operation.isCurrent()) {
    cancelSetupSidebarArrival();
    return;
  }
  if (path === pending.ownerPath) return;
  arrival = null;
  const accepted = path === pending.nextPath;
  pending.operation.dispose();
  if (accepted)
    useRightSidebarStore
      .getState()
      .open(setupSidebarDefinition, { focus: pending.focus });
}
